/**
 * Editing the points of a drawn path. §6.4.
 *
 * Pure geometry, kept apart from the overlay that uses it for the same reason
 * paths.ts is: none of it needs a canvas, and all of it is easier to be sure of
 * without one.
 *
 * Two coordinate spaces meet here, and every function says which it takes:
 *
 *   local  millimetres from the layer's own top-left, before it is turned or
 *          flipped. The space a path is shaped in.
 *   page   millimetres on the page, where the pointer is.
 *
 * Stored nodes are a third - proportions of the layer's box - and are only
 * converted to and from at the edges, by `local()` and `rebase()`.
 */

import type { PathNode } from '../model/types';
import { extent } from './paths';

export interface Point {
  x: number;
  y: number;
}

/** The parts of a layer that decide where its points are. */
export interface Placed {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  flipX?: boolean;
  flipY?: boolean;
  nodes?: PathNode[];
  closed?: boolean;
}

/** A handle closer than this to its anchor is no handle - it only makes a corner look curved. */
const NO_HANDLE = 1e-4;

/** The server keeps five places, so the editor keeps five places. */
const round = ( value: number ) => Math.round( value * 1e5 ) / 1e5;

/* ---------------------------------------------------------------
 * Between the spaces
 * ------------------------------------------------------------ */

/** The layer's points, in its own millimetres. */
export function local( layer: Placed ): PathNode[] {
  const scale = ( p: Point ) => ( { x: p.x * layer.w, y: p.y * layer.h } );

  return ( layer.nodes ?? [] ).map( ( node ) => {
    const out: PathNode = scale( node );

    if ( node.in ) out.in = scale( node.in );
    if ( node.out ) out.out = scale( node.out );

    return out;
  } );
}

/*
 * A layer is drawn as: move to its middle, turn, flip, move back by half its
 * size. That is frame.ts's definition and the press's, and these two are it
 * and its inverse. Anything else here that needs a turned layer goes through
 * them rather than doing its own trigonometry.
 */

/** A point on the page, as the layer sees it. */
export function pageToLocal( layer: Placed, point: Point ): Point {
  const a = ( layer.rotation * Math.PI ) / 180;
  const dx = point.x - ( layer.x + layer.w / 2 );
  const dy = point.y - ( layer.y + layer.h / 2 );

  const rx = dx * Math.cos( a ) + dy * Math.sin( a );
  const ry = -dx * Math.sin( a ) + dy * Math.cos( a );

  return {
    x: ( layer.flipX ? -rx : rx ) + layer.w / 2,
    y: ( layer.flipY ? -ry : ry ) + layer.h / 2,
  };
}

/** A point in the layer, on the page. */
export function localToPage( layer: Placed, point: Point ): Point {
  const a = ( layer.rotation * Math.PI ) / 180;
  const fx = ( layer.flipX ? -1 : 1 ) * ( point.x - layer.w / 2 );
  const fy = ( layer.flipY ? -1 : 1 ) * ( point.y - layer.h / 2 );

  return {
    x: layer.x + layer.w / 2 + fx * Math.cos( a ) - fy * Math.sin( a ),
    y: layer.y + layer.h / 2 + fx * Math.sin( a ) + fy * Math.cos( a ),
  };
}

/**
 * New points for a layer, and the box they now need.
 *
 * Moving a point can take the path past its old box, and a path that pokes out
 * of its layer is clipped by nothing but is selected, snapped and aligned by
 * the wrong rectangle. So the box is refitted after every change - and refitted
 * so that every point that did not move stays exactly where it was on the page,
 * which on a turned layer means moving its middle along the turned axes rather
 * than the page's.
 */
export function rebase( layer: Placed, nodes: PathNode[] ): { x: number; y: number; w: number; h: number; nodes: PathNode[] } {
  const box = extent( nodes );

  if ( ! box ) return { x: layer.x, y: layer.y, w: layer.w, h: layer.h, nodes: [] };

  // Where the new middle is, in the old layer, and then on the page.
  const middle = localToPage( layer, { x: box.x + box.w / 2, y: box.y + box.h / 2 } );

  const unit = ( p: Point ) => ( { x: round( ( p.x - box.x ) / box.w ), y: round( ( p.y - box.y ) / box.h ) } );

  return {
    x: round( middle.x - box.w / 2 ),
    y: round( middle.y - box.h / 2 ),
    w: round( box.w ),
    h: round( box.h ),
    nodes: nodes.map( ( node ) => {
      const out: PathNode = unit( node );

      if ( node.in ) out.in = unit( node.in );
      if ( node.out ) out.out = unit( node.out );

      return out;
    } ),
  };
}

/* ---------------------------------------------------------------
 * Segments
 * ------------------------------------------------------------ */

/** The four points of a segment as a cubic, straight or not. */
function cubic( from: PathNode, to: PathNode ): [ Point, Point, Point, Point ] {
  return [ from, from.out ?? from, to.in ?? to, to ];
}

function at( [ p0, p1, p2, p3 ]: [ Point, Point, Point, Point ], t: number ): Point {
  const u = 1 - t;

  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

const lerp = ( a: Point, b: Point, t: number ): Point => ( { x: a.x + ( b.x - a.x ) * t, y: a.y + ( b.y - a.y ) * t } );

const distance = ( a: Point, b: Point ) => Math.hypot( a.x - b.x, a.y - b.y );

/** How many segments a path has: one fewer than its points, or as many if it is closed. */
export function segments( nodes: PathNode[], closed: boolean ): number {
  if ( nodes.length < 2 ) return 0;

  return closed ? nodes.length : nodes.length - 1;
}

/**
 * The place on the path nearest a point - which segment, how far along it,
 * and how far away. For double-clicking a segment to add a point to it.
 *
 * Sampled, then refined. There is a closed form for the nearest point on a
 * cubic, and it is a quintic; a pointer is not that precise.
 */
export function nearest( nodes: PathNode[], closed: boolean, point: Point ): { segment: number; t: number; distance: number } | null {
  const count = segments( nodes, closed );
  let best: { segment: number; t: number; distance: number } | null = null;

  for ( let s = 0; s < count; s++ ) {
    const curve = cubic( nodes[ s ] as PathNode, nodes[ ( s + 1 ) % nodes.length ] as PathNode );

    for ( let i = 0; i <= 48; i++ ) {
      const t = i / 48;
      const d = distance( at( curve, t ), point );

      if ( ! best || d < best.distance ) best = { segment: s, t, distance: d };
    }
  }

  if ( ! best ) return null;

  // Narrow in on the best sample: a few halvings either side of it.
  const curve = cubic( nodes[ best.segment ] as PathNode, nodes[ ( best.segment + 1 ) % nodes.length ] as PathNode );
  let step = 1 / 48;

  for ( let i = 0; i < 12; i++ ) {
    step /= 2;

    const around: number = best.t;

    for ( const t of [ around - step, around + step ] ) {
      if ( t < 0 || t > 1 ) continue;

      const d = distance( at( curve, t ), point );

      if ( d < best.distance ) best = { ...best, t, distance: d };
    }
  }

  return best;
}

/**
 * A new point on a segment, without changing the path's shape.
 *
 * De Casteljau's construction: split the cubic at t and the two halves trace
 * exactly the curve the whole did. Adding a point is not supposed to move
 * anything - a customer adds one in order to move it, and should not have to
 * repair the curve first.
 */
export function split( nodes: PathNode[], closed: boolean, segment: number, t: number ): { nodes: PathNode[]; index: number } {
  const n = nodes.length;
  const from = nodes[ segment ];
  const to = nodes[ ( segment + 1 ) % n ];

  if ( ! from || ! to || segment >= segments( nodes, closed ) ) return { nodes, index: -1 };

  const next = nodes.slice();
  const index = segment + 1;

  if ( ! from.out && ! to.in ) {
    // A straight segment gets a corner, and stays straight on both sides.
    next.splice( index, 0, lerp( from, to, t ) );

    return { nodes: next, index };
  }

  const [ p0, p1, p2, p3 ] = cubic( from, to );
  const q0 = lerp( p0, p1, t );
  const q1 = lerp( p1, p2, t );
  const q2 = lerp( p2, p3, t );
  const r0 = lerp( q0, q1, t );
  const r1 = lerp( q1, q2, t );
  const s = lerp( r0, r1, t );

  next[ segment ] = clean( { ...from, out: q0 } );
  next[ ( segment + 1 ) % n ] = clean( { ...to, in: q2 } );
  next.splice( index, 0, clean( { x: s.x, y: s.y, in: r0, out: r1 } ) );

  return { nodes: next, index };
}

/** Drops a handle sitting on its own anchor, which is a corner pretending. */
function clean( node: PathNode ): PathNode {
  const out: PathNode = { x: node.x, y: node.y };

  if ( node.in && distance( node.in, node ) > NO_HANDLE ) out.in = node.in;
  if ( node.out && distance( node.out, node ) > NO_HANDLE ) out.out = node.out;

  return out;
}

/* ---------------------------------------------------------------
 * One point at a time
 * ------------------------------------------------------------ */

/** Whether a point is smooth: two handles, on one line through it. */
export function isSmooth( node: PathNode ): boolean {
  if ( ! node.in || ! node.out ) return false;

  const ax = node.in.x - node.x;
  const ay = node.in.y - node.y;
  const bx = node.out.x - node.x;
  const by = node.out.y - node.y;
  const lengths = Math.hypot( ax, ay ) * Math.hypot( bx, by );

  // Opposite directions: the cross product near nothing, the dot product negative.
  return lengths > 0 && Math.abs( ax * by - ay * bx ) / lengths < 0.035 && ax * bx + ay * by < 0;
}

/**
 * Corner to smooth, or back. Alt-click, as everywhere.
 *
 * A new smooth point takes its direction from its neighbours - the line from
 * the one before to the one after - and its handles a third of the way to
 * each, which is the length that turns a polygon into the gentlest curve
 * through the same points.
 */
export function toggleSmooth( nodes: PathNode[], closed: boolean, index: number ): PathNode[] {
  const node = nodes[ index ];

  if ( ! node ) return nodes;

  const next = nodes.slice();

  if ( node.in || node.out ) {
    next[ index ] = { x: node.x, y: node.y };

    return next;
  }

  const n = nodes.length;
  const before = index > 0 ? nodes[ index - 1 ] : closed ? nodes[ n - 1 ] : undefined;
  const after = index < n - 1 ? nodes[ index + 1 ] : closed ? nodes[ 0 ] : undefined;

  const from = before ?? node;
  const to = after ?? node;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot( dx, dy );

  if ( length === 0 ) return nodes;

  const ux = dx / length;
  const uy = dy / length;
  const back = before ? distance( before, node ) / 3 : 0;
  const ahead = after ? distance( after, node ) / 3 : 0;

  next[ index ] = clean( {
    x: node.x,
    y: node.y,
    in: { x: node.x - ux * back, y: node.y - uy * back },
    out: { x: node.x + ux * ahead, y: node.y + uy * ahead },
  } );

  return next;
}

/** Takes a point out. A path keeps at least two, or it is not a path. */
export function remove( nodes: PathNode[], index: number ): PathNode[] {
  if ( nodes.length <= 2 || ! nodes[ index ] ) return nodes;

  return nodes.filter( ( _node, i ) => i !== index );
}

/** An anchor moved, carrying its handles with it. */
export function moveAnchor( nodes: PathNode[], index: number, to: Point ): PathNode[] {
  const node = nodes[ index ];

  if ( ! node ) return nodes;

  const dx = to.x - node.x;
  const dy = to.y - node.y;
  const shift = ( p?: Point ) => ( p ? { x: p.x + dx, y: p.y + dy } : undefined );
  const next = nodes.slice();

  const moved: PathNode = { x: to.x, y: to.y };
  const into = shift( node.in );
  const out = shift( node.out );

  if ( into ) moved.in = into;
  if ( out ) moved.out = out;

  next[ index ] = moved;

  return next;
}

/**
 * A handle moved.
 *
 * On a smooth point the other handle turns to stay opposite, keeping its own
 * length, so the curve stays smooth through the point. Holding Alt breaks
 * that and moves the one handle alone - which is how a smooth point becomes a
 * sharp one on purpose, and the same key every vector tool uses for it.
 */
export function moveHandle( nodes: PathNode[], index: number, which: 'in' | 'out', to: Point, alone: boolean ): PathNode[] {
  const node = nodes[ index ];

  if ( ! node ) return nodes;

  const other = which === 'in' ? 'out' : 'in';
  const next = nodes.slice();
  const moved: PathNode = { ...node, [ which ]: { x: to.x, y: to.y } };
  const opposite = node[ other ];

  if ( ! alone && opposite && isSmooth( node ) ) {
    const length = distance( opposite, node );
    const dx = node.x - to.x;
    const dy = node.y - to.y;
    const pulled = Math.hypot( dx, dy );

    if ( pulled > 0 ) {
      moved[ other ] = { x: node.x + ( dx / pulled ) * length, y: node.y + ( dy / pulled ) * length };
    }
  }

  next[ index ] = moved;

  return next;
}

/* ---------------------------------------------------------------
 * From a library shape
 * ------------------------------------------------------------ */

/**
 * The points of a library shape, so it can be edited like a drawing.
 *
 * Reads only the M, L, C and Z that library.ts writes. A shape with a hole -
 * a ring, a frame - is two paths, and a drawing is one, so those return null
 * and the control that would convert them says why.
 *
 * Takes the path in the layer's own millimetres and returns points in the
 * same, ready for `rebase`.
 */
export function fromPath( d: string ): { nodes: PathNode[]; closed: boolean } | null {
  const steps = ( d.match( /[MLCZ][^MLCZ]*/gi ) ?? [] ).map( ( token ) => ( {
    kind: ( token[ 0 ] ?? 'M' ).toUpperCase(),
    nums: ( token.slice( 1 ).match( /-?[\d.]+(?:e-?\d+)?/gi ) ?? [] ).map( Number ),
  } ) );

  if ( steps.filter( ( s ) => s.kind === 'M' ).length !== 1 || steps[ 0 ]?.kind !== 'M' ) return null;

  const nodes: PathNode[] = [];
  let closed = false;

  for ( const step of steps ) {
    const n = step.nums;

    if ( step.kind === 'M' || step.kind === 'L' ) {
      nodes.push( { x: n[ 0 ] ?? 0, y: n[ 1 ] ?? 0 } );
    } else if ( step.kind === 'C' ) {
      const last = nodes[ nodes.length - 1 ];

      if ( last ) nodes[ nodes.length - 1 ] = { ...last, out: { x: n[ 0 ] ?? 0, y: n[ 1 ] ?? 0 } };

      nodes.push( { x: n[ 4 ] ?? 0, y: n[ 5 ] ?? 0, in: { x: n[ 2 ] ?? 0, y: n[ 3 ] ?? 0 } } );
    } else if ( step.kind === 'Z' ) {
      closed = true;
    }
  }

  /*
   * A closed shape usually draws its last segment back to where it began, so
   * the first point appears twice. Kept, that is a point on top of a point: two
   * anchors to drag where the customer sees one, and a crease in the curve
   * where they meet. The two become one, keeping the handle each brought.
   */
  const first = nodes[ 0 ];
  const last = nodes[ nodes.length - 1 ];

  if ( closed && first && last && nodes.length > 2 && distance( first, last ) < 1e-3 ) {
    nodes.pop();
    nodes[ 0 ] = last.in ? { ...first, in: last.in } : first;
  }

  if ( nodes.length < 2 ) return null;

  return { nodes: nodes.map( clean ), closed };
}
